import React, { useState, useEffect, useRef, useMemo } from 'react';
import { GlobeAltIcon, ChevronDownIcon, MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useCountryCurrency } from '../../context/CountryCurrencyContext';

/**
 * Convert an ISO 3166-1 alpha-2 country code (e.g. "UG") into a flag emoji.
 * Works by shifting each letter into the Regional Indicator Symbol range.
 */
const codeToFlag = (code) => {
  if (!code || code.length !== 2) return '';
  const A = 0x1f1e6;
  const chars = code.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(...chars);
};

/**
 * Comprehensive country list grouped by region.
 * Each entry: { name, code (ISO alpha-2) }
 */
const COUNTRY_GROUPS = [
  {
    label: 'East Africa',
    countries: [
      { name: 'Uganda', code: 'UG' },
      { name: 'Kenya', code: 'KE' },
      { name: 'Tanzania', code: 'TZ' },
      { name: 'Rwanda', code: 'RW' },
      { name: 'Burundi', code: 'BI' },
      { name: 'South Sudan', code: 'SS' },
      { name: 'Ethiopia', code: 'ET' },
      { name: 'Eritrea', code: 'ER' },
      { name: 'Djibouti', code: 'DJ' },
      { name: 'Somalia', code: 'SO' },
    ],
  },
  {
    label: 'Central & Southern Africa',
    countries: [
      { name: 'Democratic Republic of the Congo', code: 'CD' },
      { name: 'Republic of the Congo', code: 'CG' },
      { name: 'Central African Republic', code: 'CF' },
      { name: 'Chad', code: 'TD' },
      { name: 'Cameroon', code: 'CM' },
      { name: 'Gabon', code: 'GA' },
      { name: 'Equatorial Guinea', code: 'GQ' },
      { name: 'São Tomé and Príncipe', code: 'ST' },
      { name: 'Angola', code: 'AO' },
      { name: 'Zambia', code: 'ZM' },
      { name: 'Malawi', code: 'MW' },
      { name: 'Mozambique', code: 'MZ' },
      { name: 'Zimbabwe', code: 'ZW' },
      { name: 'Botswana', code: 'BW' },
      { name: 'Namibia', code: 'NA' },
      { name: 'South Africa', code: 'ZA' },
      { name: 'Lesotho', code: 'LS' },
      { name: 'Eswatini', code: 'SZ' },
      { name: 'Madagascar', code: 'MG' },
      { name: 'Mauritius', code: 'MU' },
      { name: 'Seychelles', code: 'SC' },
      { name: 'Comoros', code: 'KM' },
    ],
  },
  {
    label: 'West Africa',
    countries: [
      { name: 'Nigeria', code: 'NG' },
      { name: 'Ghana', code: 'GH' },
      { name: "Côte d'Ivoire", code: 'CI' },
      { name: 'Senegal', code: 'SN' },
      { name: 'Mali', code: 'ML' },
      { name: 'Burkina Faso', code: 'BF' },
      { name: 'Niger', code: 'NE' },
      { name: 'Guinea', code: 'GN' },
      { name: 'Guinea-Bissau', code: 'GW' },
      { name: 'Sierra Leone', code: 'SL' },
      { name: 'Liberia', code: 'LR' },
      { name: 'Togo', code: 'TG' },
      { name: 'Benin', code: 'BJ' },
      { name: 'The Gambia', code: 'GM' },
      { name: 'Mauritania', code: 'MR' },
      { name: 'Cape Verde', code: 'CV' },
    ],
  },
  {
    label: 'North Africa',
    countries: [
      { name: 'Egypt', code: 'EG' },
      { name: 'Morocco', code: 'MA' },
      { name: 'Algeria', code: 'DZ' },
      { name: 'Tunisia', code: 'TN' },
      { name: 'Libya', code: 'LY' },
      { name: 'Sudan', code: 'SD' },
    ],
  },
  {
    label: 'Middle East',
    countries: [
      { name: 'United Arab Emirates', code: 'AE' },
      { name: 'Saudi Arabia', code: 'SA' },
      { name: 'Qatar', code: 'QA' },
      { name: 'Kuwait', code: 'KW' },
      { name: 'Bahrain', code: 'BH' },
      { name: 'Oman', code: 'OM' },
      { name: 'Jordan', code: 'JO' },
      { name: 'Lebanon', code: 'LB' },
      { name: 'Israel', code: 'IL' },
      { name: 'Palestine', code: 'PS' },
      { name: 'Iraq', code: 'IQ' },
      { name: 'Iran', code: 'IR' },
      { name: 'Turkey', code: 'TR' },
      { name: 'Yemen', code: 'YE' },
    ],
  },
  {
    label: 'Asia Pacific',
    countries: [
      { name: 'India', code: 'IN' },
      { name: 'Pakistan', code: 'PK' },
      { name: 'Bangladesh', code: 'BD' },
      { name: 'Sri Lanka', code: 'LK' },
      { name: 'Nepal', code: 'NP' },
      { name: 'China', code: 'CN' },
      { name: 'Japan', code: 'JP' },
      { name: 'South Korea', code: 'KR' },
      { name: 'Singapore', code: 'SG' },
      { name: 'Malaysia', code: 'MY' },
      { name: 'Thailand', code: 'TH' },
      { name: 'Philippines', code: 'PH' },
      { name: 'Indonesia', code: 'ID' },
      { name: 'Vietnam', code: 'VN' },
      { name: 'Cambodia', code: 'KH' },
      { name: 'Myanmar', code: 'MM' },
      { name: 'Laos', code: 'LA' },
      { name: 'Australia', code: 'AU' },
      { name: 'New Zealand', code: 'NZ' },
      { name: 'Fiji', code: 'FJ' },
    ],
  },
  {
    label: 'Europe',
    countries: [
      { name: 'United Kingdom', code: 'GB' },
      { name: 'Ireland', code: 'IE' },
      { name: 'France', code: 'FR' },
      { name: 'Germany', code: 'DE' },
      { name: 'Spain', code: 'ES' },
      { name: 'Italy', code: 'IT' },
      { name: 'Portugal', code: 'PT' },
      { name: 'Netherlands', code: 'NL' },
      { name: 'Belgium', code: 'BE' },
      { name: 'Switzerland', code: 'CH' },
      { name: 'Austria', code: 'AT' },
      { name: 'Sweden', code: 'SE' },
      { name: 'Norway', code: 'NO' },
      { name: 'Denmark', code: 'DK' },
      { name: 'Finland', code: 'FI' },
      { name: 'Iceland', code: 'IS' },
      { name: 'Poland', code: 'PL' },
      { name: 'Czechia', code: 'CZ' },
      { name: 'Slovakia', code: 'SK' },
      { name: 'Hungary', code: 'HU' },
      { name: 'Romania', code: 'RO' },
      { name: 'Bulgaria', code: 'BG' },
      { name: 'Greece', code: 'GR' },
      { name: 'Croatia', code: 'HR' },
      { name: 'Serbia', code: 'RS' },
      { name: 'Slovenia', code: 'SI' },
      { name: 'Bosnia and Herzegovina', code: 'BA' },
      { name: 'Albania', code: 'AL' },
      { name: 'North Macedonia', code: 'MK' },
      { name: 'Montenegro', code: 'ME' },
      { name: 'Kosovo', code: 'XK' },
      { name: 'Belarus', code: 'BY' },
      { name: 'Moldova', code: 'MD' },
      { name: 'Ukraine', code: 'UA' },
      { name: 'Russia', code: 'RU' },
      { name: 'Georgia', code: 'GE' },
      { name: 'Armenia', code: 'AM' },
      { name: 'Azerbaijan', code: 'AZ' },
      { name: 'Estonia', code: 'EE' },
      { name: 'Latvia', code: 'LV' },
      { name: 'Lithuania', code: 'LT' },
    ],
  },
  {
    label: 'Americas',
    countries: [
      { name: 'United States', code: 'US' },
      { name: 'Canada', code: 'CA' },
      { name: 'Mexico', code: 'MX' },
      { name: 'Brazil', code: 'BR' },
      { name: 'Argentina', code: 'AR' },
      { name: 'Colombia', code: 'CO' },
      { name: 'Peru', code: 'PE' },
      { name: 'Chile', code: 'CL' },
      { name: 'Venezuela', code: 'VE' },
      { name: 'Ecuador', code: 'EC' },
      { name: 'Bolivia', code: 'BO' },
      { name: 'Paraguay', code: 'PY' },
      { name: 'Uruguay', code: 'UY' },
      { name: 'Cuba', code: 'CU' },
      { name: 'Dominican Republic', code: 'DO' },
      { name: 'Jamaica', code: 'JM' },
      { name: 'Trinidad and Tobago', code: 'TT' },
      { name: 'Barbados', code: 'BB' },
      { name: 'Bahamas', code: 'BS' },
      { name: 'Panama', code: 'PA' },
      { name: 'Costa Rica', code: 'CR' },
      { name: 'Guatemala', code: 'GT' },
      { name: 'Honduras', code: 'HN' },
      { name: 'El Salvador', code: 'SV' },
      { name: 'Nicaragua', code: 'NI' },
    ],
  },
];

// Flatten for search & quick lookups
const ALL_COUNTRIES = COUNTRY_GROUPS.flatMap(g => g.countries);
const COUNTRY_BY_CODE = Object.fromEntries(ALL_COUNTRIES.map(c => [c.code, c]));

const CountrySelector = ({ variant = 'desktop' }) => {
  const { country: selected, loading, autoDetected, selectCountry } = useCountryCurrency();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const detecting = loading && !selected;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return COUNTRY_GROUPS;
    const q = search.toLowerCase().trim();
    return COUNTRY_GROUPS
      .map(g => ({
        ...g,
        countries: g.countries.filter(c =>
          c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.countries.length > 0);
  }, [search]);

  const handleSelect = (country) => {
    selectCountry(country);
    setOpen(false);
    setSearch('');
  };

  // ── Mobile variant ──────────────────────────────────────────────
  if (variant === 'mobile') {
    return (
      <div className="px-2" ref={containerRef}>
        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-semibold text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-all"
          >
            <span className="flex items-center gap-2">
              {selected ? (
                <>
                  <span className="text-lg leading-none">{codeToFlag(selected.code)}</span>
                  <span>{selected.name}</span>
                </>
              ) : (
                <>
                  <GlobeAltIcon className="h-5 w-5 text-teal-500" />
                  <span>{detecting ? 'Detecting…' : 'Select country'}</span>
                </>
              )}
            </span>
            <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="mt-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg max-h-72 overflow-y-auto">
              <div className="sticky top-0 p-2 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search country…"
                    className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  />
                </div>
              </div>
              <div className="py-1">
                {filteredGroups.length === 0 && (
                  <p className="px-4 py-3 text-sm text-gray-400">No countries found</p>
                )}
                {filteredGroups.map(g => (
                  <div key={g.label}>
                    <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{g.label}</p>
                    {g.countries.map(c => (
                      <button
                        key={c.code}
                        onClick={() => handleSelect(c)}
                        className={`flex items-center justify-between w-full px-4 py-2 text-sm transition-colors ${
                          selected?.code === c.code
                            ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-semibold'
                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-base leading-none">{codeToFlag(c.code)}</span>
                          {c.name}
                        </span>
                        {selected?.code === c.code && <CheckIcon className="h-4 w-4" />}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {autoDetected && selected && (
          <p className="mt-1.5 px-4 text-[11px] text-gray-400">
            Auto-detected from your location. Tap to change.
          </p>
        )}
      </div>
    );
  }

  // ── Desktop variant ─────────────────────────────────────────────
  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(!open)}
        title={selected ? `${selected.name}${autoDetected ? ' (auto-detected)' : ''}` : detecting ? 'Detecting your country…' : 'Select country'}
        className="group flex items-center gap-1.5 px-1.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-200 transition-all duration-300 hover:text-gray-900 dark:hover:text-white"
      >
        {selected ? (
          <>
            <span className="text-sm leading-none">{codeToFlag(selected.code)}</span>
            <span className="max-w-[80px] truncate">{selected.name}</span>
          </>
        ) : (
          <>
            <GlobeAltIcon className="h-4 w-4 text-teal-500 group-hover:scale-110 transition-transform" />
            <span className="text-gray-400">{detecting ? 'Detecting…' : 'Select'}</span>
          </>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false); setSearch(''); }} />
          <div className="absolute right-0 mt-2 w-72 rounded-2xl backdrop-blur-xl bg-white/95 dark:bg-gray-900/95 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)] z-50 border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
            {/* Search */}
            <div className="p-3 border-b border-gray-100 dark:border-gray-800">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search country…"
                  className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                />
              </div>
            </div>

            {/* Country list */}
            <div className="max-h-72 overflow-y-auto py-1">
              {filteredGroups.length === 0 && (
                <p className="px-4 py-3 text-sm text-gray-400">No countries found</p>
              )}
              {filteredGroups.map(g => (
                <div key={g.label}>
                  <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm">
                    {g.label}
                  </p>
                  {g.countries.map(c => (
                    <button
                      key={c.code}
                      onClick={() => handleSelect(c)}
                      className={`flex items-center justify-between w-full px-4 py-2 text-sm transition-colors ${
                        selected?.code === c.code
                          ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-semibold'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-base leading-none">{codeToFlag(c.code)}</span>
                        {c.name}
                      </span>
                      {selected?.code === c.code && <CheckIcon className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {autoDetected && selected && (
              <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-400 flex items-center gap-1.5">
                <GlobeAltIcon className="h-3.5 w-3.5" />
                Auto-detected from your location
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CountrySelector;
